/**
 * NotificationSkeleton Component
 *
 * Loading skeleton for NotificationDropdown.
 * Shows 3 placeholder notification items with animated pulse effect.
 */

import React from 'react';

import type { NotificationSkeletonProps } from './NotificationSkeleton.types';

import { Skeleton, SkeletonAvatar } from '@/components/ui/Progress';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Single notification skeleton item matching NotificationItem layout.
 */
const NotificationSkeletonItem: React.FC = () => {
  return (
    <div className="flex items-start gap-3 p-4">
      {/* Avatar skeleton */}
      <SkeletonAvatar size="lg" animation="pulse" className="flex-shrink-0" />

      {/* Content skeleton */}
      <div className="flex-1 min-w-0 space-y-2">
        {/* Title */}
        <Skeleton
          variant="text"
          width="40%"
          height={14}
          animation="pulse"
          className="bg-contrast-600"
        />
        {/* Message - 2 lines */}
        <Skeleton
          variant="text"
          width="90%"
          height={14}
          animation="pulse"
          className="bg-contrast-600"
        />
        <Skeleton
          variant="text"
          width="60%"
          height={14}
          animation="pulse"
          className="bg-contrast-600"
        />
        {/* Timestamp */}
        <Skeleton
          variant="text"
          width="20%"
          height={12}
          animation="pulse"
          className="bg-contrast-600 mt-1"
        />
      </div>

      {/* Action button skeleton */}
      <div className="flex-shrink-0">
        <Skeleton
          variant="rounded"
          width={32}
          height={32}
          animation="pulse"
          className="bg-contrast-600"
        />
      </div>
    </div>
  );
};

/**
 * NotificationSkeleton shows loading placeholders for notifications.
 * Matches the layout of NotificationItem for seamless loading transitions.
 */
export const NotificationSkeleton: React.FC<NotificationSkeletonProps> = ({
  count = 3,
  className = '',
}) => {
  return (
    <div className={className} role="status" aria-label="Loading notifications">
      {Array.from({ length: count }).map((_, index) => (
        <NotificationSkeletonItem key={index} />
      ))}
      <span className="sr-only">Loading notifications...</span>
    </div>
  );
};

export default NotificationSkeleton;
